/**
 * Hotel Name Autocorrect / Typo Tolerance – fallback layer on top of Hotel Name Index.
 * Only runs when there is no strong (EXACT/PREFIX/PHRASE) match from the index.
 * No API or booking flow changes.
 */

import { normalizeHotelName } from './searchRelevance';

export const FUZZY_MATCH_TYPE = 'FUZZY';

const SIMILARITY_STRONG = 0.9;
const SIMILARITY_MODERATE = 0.8;
const SIMILARITY_THRESHOLD = 0.7;

/** Min normalized query length to run autocorrect (avoid false positives on "h", "hi", etc.). */
export const MIN_QUERY_LENGTH_FOR_AUTOCORRECT = 5;

/** Boost for fuzzy match: weaker than PREFIX (-30) and PHRASE (-50), so exact/index matches still win. */
export const AUTOCORRECT_BOOST = -20;

/**
 * Levenshtein distance between two strings.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function levenshteinDistance(a, b) {
  if (!a) return (b || '').length;
  if (!b) return a.length;
  const m = a.length;
  const n = b.length;
  const d = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i][0] = i;
  for (let j = 0; j <= n; j++) d[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
    }
  }
  return d[m][n];
}

/**
 * String similarity in [0, 1]. Uses Levenshtein; 1 = identical.
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export function calculateStringSimilarity(a, b) {
  if (a == null || b == null) return 0;
  const s = String(a).trim();
  const t = String(b).trim();
  if (s === t) return 1;
  if (!s || !t) return 0;
  const maxLen = Math.max(s.length, t.length);
  const distance = levenshteinDistance(s, t);
  return 1 - distance / maxLen;
}

/**
 * Token-level similarity: for each query token, best match against candidate tokens; average.
 * @param {string[]} queryTokens
 * @param {string[]} candidateTokens
 * @returns {number}
 */
export function calculateTokenSimilarity(queryTokens, candidateTokens) {
  if (!queryTokens?.length || !candidateTokens?.length) return 0;
  let sum = 0;
  for (const qt of queryTokens) {
    let best = 0;
    for (const ct of candidateTokens) {
      const sim = calculateStringSimilarity(qt, ct);
      if (sim > best) best = sim;
    }
    sum += best;
  }
  return sum / queryTokens.length;
}

/**
 * Best fuzzy match for query against hotel index. Only returns if above SIMILARITY_THRESHOLD.
 * Call whenever intent is hotel search and query is long enough; apply boost per-hotel only when
 * that hotel has no strong index match (so a global strong match for another hotel does not block).
 * @param {string} query
 * @param {Record<string, { hotelId: string, dedupKey: string, canonicalName: string, normalizedCanonicalName: string, normalizedAliases: string[], tokenSet: Set<string> }>} hotelIndex
 * @returns {null | { dedupKey: string, hotelId: string, originalQuery: string, correctedQuery: string, suggestedQuery: string, matchedValue: string, candidateSource: 'canonical'|'alias', similarityScore: number, confidence: number, matchType: string }}
 */
export function getBestFuzzyHotelMatch(query, hotelIndex) {
  if (!query || typeof query !== 'string' || !hotelIndex || typeof hotelIndex !== 'object') {
    return null;
  }

  const originalQuery = String(query).trim();
  const normalizedQuery = normalizeHotelName(query);
  if (!normalizedQuery || normalizedQuery.length < MIN_QUERY_LENGTH_FOR_AUTOCORRECT) {
    return null;
  }

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  let best = null;
  let bestScore = SIMILARITY_THRESHOLD;

  for (const dedupKey of Object.keys(hotelIndex)) {
    const entry = hotelIndex[dedupKey];
    if (!entry) continue;

    const canonicalNorm = entry.normalizedCanonicalName;
    const aliasNorms = entry.normalizedAliases || [];
    const candidateTokens = [...(entry.tokenSet || [])];

    const pairs = [[canonicalNorm, 'canonical']];
    aliasNorms.forEach((a) => {
      if (a && a !== canonicalNorm) pairs.push([a, 'alias']);
    });

    for (const [candidate, source] of pairs) {
      if (!candidate) continue;

      const stringSim = calculateStringSimilarity(normalizedQuery, candidate);
      const tokenSim = calculateTokenSimilarity(queryTokens, candidateTokens);
      const combined = stringSim * 0.7 + tokenSim * 0.3;

      if (combined > bestScore) {
        bestScore = combined;
        const suggestedQuery = entry.canonicalName || candidate;
        best = {
          dedupKey,
          hotelId: entry.hotelId,
          originalQuery,
          correctedQuery: suggestedQuery,
          suggestedQuery,
          matchedValue: candidate,
          candidateSource: source,
          similarityScore: combined,
          confidence: combined,
          matchType: FUZZY_MATCH_TYPE,
        };
      }
    }
  }

  return best;
}

/**
 * Whether confidence is high enough to apply autocorrect boost.
 */
export function isAutocorrectConfidenceHigh(result) {
  return Boolean(result && result.confidence >= SIMILARITY_MODERATE);
}
